/**
 * API client for communicating with Azure Functions backend.
 * Replaces direct Supabase client calls.
 */

import { globalQueue } from './requestQueue.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

let getTokenFn: (() => Promise<string | null>) | null = null;

/**
 * Register the Clerk getToken function. Called once from ClerkProvider setup.
 */
export function setTokenProvider(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

/**
 * Authenticated fetch wrapper that automatically includes the Clerk JWT.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options?: RequestInit & { skipAuth?: boolean; skipQueue?: boolean },
): Promise<T> {
  const { skipAuth, skipQueue, ...fetchOptions } = options || {};

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (!skipAuth && getTokenFn) {
    const token = await getTokenFn();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const doFetch = () =>
    fetch(`${API_BASE_URL}${path}`, {
      ...fetchOptions,
      headers,
      signal: fetchOptions.signal,
    });

  const method = (fetchOptions.method || 'GET').toUpperCase();
  const response = skipQueue
    ? await doFetch()
    : await globalQueue.enqueue(path, method, doFetch);

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const error = new ApiError(body.error || response.statusText, response.status, body.code, body.requestId);
    if (body.requestId) {
      lastRequestId = body.requestId;
    }
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/** Most recent requestId returned by the API for a 500 error. */
export let lastRequestId: string | undefined;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Validated request helpers — validate body with a Zod schema before sending.
import type { ZodType } from 'zod';

export async function validatedPost<TSchema extends ZodType, TResponse = unknown>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(body);
  return api.post<TResponse>(path, parsed, options);
}

export async function validatedPatch<TSchema extends ZodType, TResponse = unknown>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(body);
  return api.patch<TResponse>(path, parsed, options);
}

/**
 * Validated GET — validates path parameters with a Zod schema before
 * interpolating them into the URL template.
 *
 * Usage:
 *   validatedGet('/analyses/:id', UuidParamSchema, { id: someId })
 */
export async function validatedGet<TSchema extends ZodType, TResponse = unknown>(
  pathTemplate: string,
  schema: TSchema,
  params: Record<string, string>,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(params) as Record<string, string>;
  const path = pathTemplate.replace(/:([a-zA-Z_]+)/g, (_, key) => {
    if (!(key in parsed)) throw new Error(`Missing path parameter: ${key}`);
    return encodeURIComponent(parsed[key]);
  });
  return api.get<TResponse>(path, options);
}

// Convenience methods
export const api = {
  get: <T = unknown>(path: string, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T = unknown>(path: string, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
